const pad2 = (value) => String(value).padStart(2, "0");

const patterns = [
  {regex: /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/, order: ["y", "m", "d"]},
  {regex: /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/, order: ["m", "d", "y"]},
];

const buildParts = (date) => {
  const y = date.getUTCFullYear();
  const m = pad2(date.getUTCMonth() + 1);
  const d = pad2(date.getUTCDate());
  return {
    canonical: `${y}/${m}/${d}`, // requested display format
    inputValue: `${y}-${m}-${d}`, // html date input
    year: y,
    month: m,
    day: d,
  };
};

/**
 * Normalize a date string to yyyy/MM/dd and provide an HTML-friendly value.
 * Accepts yyyy/MM/dd, yyyy-MM-dd, MM/dd/yyyy, MM-dd-yyyy.
 */
export function normalizeDateInput(value) {
  if (!value) {
    return {isValid: false, canonical: "", inputValue: "", error: "Date is required"};
  }
  if (value instanceof Date && !isNaN(value.getTime())) {
    const parts = buildParts(new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())));
    return {isValid: true, ...parts};
  }

  const trimmed = String(value).trim();
  const yearCheck = trimmed.match(/^(\d{5,})[-/]/);
  if (yearCheck) {
    return {isValid: false, canonical: "", inputValue: "", error: "Year must be 4 digits"};
  }
  for (const {regex, order} of patterns) {
    const match = trimmed.match(regex);
    if (!match) continue;
    const parts = {};
    order.forEach((key, idx) => (parts[key] = parseInt(match[idx + 1], 10)));

    if (String(parts.y)?.length !== 4) {
      return {isValid: false, canonical: "", inputValue: "", error: "Year must be 4 digits"};
    }
    if (parts.y < 1900 || parts.y > 2100) {
      return {isValid: false, canonical: "", inputValue: "", error: "Year out of range"};
    }
    if (parts.m < 1 || parts.m > 12) {
      return {isValid: false, canonical: "", inputValue: "", error: "Month must be 1-12"};
    }
    if (parts.d < 1 || parts.d > 31) {
      return {isValid: false, canonical: "", inputValue: "", error: "Day must be 1-31"};
    }

    const date = new Date(Date.UTC(parts.y, parts.m - 1, parts.d));
    // Validate actual calendar date (e.g., reject Feb 30)
    if (
      date.getUTCFullYear() !== parts.y ||
      date.getUTCMonth() + 1 !== parts.m ||
      date.getUTCDate() !== parts.d
    ) {
      return {isValid: false, canonical: "", inputValue: "", error: "Invalid calendar date"};
    }

    const built = buildParts(date);
    return {isValid: true, ...built};
  }

  return {
    isValid: false,
    canonical: "",
    inputValue: "",
    error: "Use yyyy/MM/dd or MM/dd/yyyy",
  };
}

/**
 * Convert stored date value to html date input value.
 */
export function toInputDateValue(value) {
  if (!value) return "";
  const parsed = normalizeDateInput(value);
  return parsed.isValid ? parsed.inputValue : "";
}

